/**
 * SharePoint Connection - Zod Schemas
 *
 * Validation schemas for SharePoint file browsing and upload operations.
 */

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * SharePoint Auth Callback - Request body
 */
export const DocumentSharePointAuthBodySchema = z.object({
  code: z.string().min(1, "Authorization code is required"),
  redirectUri: z.string().url("Invalid redirect URI")
});

/**
 * List SharePoint Sites - Query params
 */
export const DocumentListSharePointSitesParamsSchema = z.object({
  connectionId: z.string().uuid()
});

/**
 * List SharePoint Files - Query params
 *
 * `pageToken` is the `nextPageToken` a previous page returned. The response
 * schema has always carried that token and the params schema had no field to
 * send it back on, so the second page of a folder was unreachable: a 400-file
 * folder served its first 100 and stopped. It mirrors
 * `DocumentListGoogleDriveFilesParamsSchema.pageToken`, and it is the
 * caller-facing half of the `pageToken` argument the Graph client already
 * accepts.
 */
export const DocumentListSharePointFilesParamsSchema = z.object({
  connectionId: z.string().uuid(),
  siteId: z.string().min(1, "Site ID is required"),
  folderId: z.string().optional(),
  pageToken: z.string().optional()
});

/**
 * Upload SharePoint Files - Request body
 */
export const DocumentUploadSharePointBodySchema = z.object({
  connectionId: z.string().uuid(),
  refreshToken: z.string().optional(),
  siteId: z.string().min(1, "Site ID is required"),
  fileIds: z.array(z.string()).min(1, "At least one file ID is required"),
  parentId: z.string().optional()
});

/**
 * Refresh SharePoint Token - Request body
 */
export const DocumentRefreshSharePointTokenBodySchema = z.object({
  refreshToken: z.string().min(1, "Refresh token is required")
});

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * SharePoint Auth Response
 */
export const DocumentSharePointAuthResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresIn: z.number().optional(),
  metadata: z.record(z.string(), z.unknown()).optional()
});

/**
 * SharePoint Site
 */
export const SharePointSiteSchema = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string(),
  webUrl: z.string()
});

/**
 * List SharePoint Sites Response
 */
export const DocumentListSharePointSitesResponseSchema = z.object({
  sites: z.array(SharePointSiteSchema)
});

/**
 * SharePoint File Response
 */
export const SharePointFileResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  folder: z.object({ childCount: z.number().optional() }).optional(),
  size: z.number().optional(),
  lastModifiedDateTime: z.string().optional(),
  webUrl: z.string().optional(),
  mimeType: z.string().optional()
});

/**
 * List SharePoint Files Response
 */
export const DocumentListSharePointFilesResponseSchema = z.object({
  files: z.array(SharePointFileResponseSchema),
  nextPageToken: z.string().optional(),
  currentFolder: z.string()
});

/**
 * Upload SharePoint Response
 */
export const DocumentUploadSharePointResponseSchema = z.object({
  message: z.string(),
  documentIds: z.array(z.string()).optional()
});

/**
 * Refresh SharePoint Token Response
 */
export const DocumentRefreshSharePointTokenResponseSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number().optional()
});
